import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  useBringFriendEligibility,
  useMyFriendInvites,
  useReserveInvite,
  useRemintToken,
  useRevokeInvite,
  useUpdateInvite,
  activeInvitesInWindow,
  MAX_FRIEND_SLOTS,
  type FriendInviteRow,
} from "@/hooks/useBringFriend";
import {
  buildBringFriendInviteLink,
  shareBringFriendInvite,
} from "@/lib/invitation";
import { capture } from "@/services/posthog";
import { toast } from "@/hooks/use-toast";
import {
  UserPlus,
  Share2,
  X,
  Check,
  Clock,
  Gift,
  Copy,
  Link as LinkIcon,
  Pencil,
} from "lucide-react";

interface BringFriendPanelProps {
  eventId: string;
  ticketId: string;
  eventTitle: string;
  embedded?: boolean;
}

export default function BringFriendPanel({
  eventId,
  ticketId,
  eventTitle,
  embedded = false,
}: BringFriendPanelProps) {
  const { data: eligibility } = useBringFriendEligibility(eventId);
  const { data: allInvites } = useMyFriendInvites(
    eligibility?.eligible ?? false,
  );
  const reserveMutation = useReserveInvite();
  const remintMutation = useRemintToken();
  const revokeMutation = useRevokeInvite();
  const updateMutation = useUpdateInvite();

  const [friendName, setFriendName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  // Tokens minted in this session, keyed by invite id — lets Share stay a
  // synchronous click (mobile browsers block window.open after an await).
  const [tokens, setTokens] = useState<Record<string, string>>({});

  const isFullName = (name: string) => name.trim().split(/\s+/).length >= 2;

  if (!eligibility?.eligible) return null;

  // Seats are scoped to the current campaign window, matching the server.
  const active = activeInvitesInWindow(allInvites, eligibility);
  const maxSeats = eligibility.max_seats ?? MAX_FRIEND_SLOTS;
  const remainingSlots = Math.max(0, maxSeats - active.length);
  const eventInvites = active.filter((i) => i.event_id === eventId);

  const handleCreateInvite = async () => {
    if (remainingSlots <= 0) return;
    try {
      const result = await reserveMutation.mutateAsync({
        eventId,
        sourceTicketId: ticketId,
        friendFullName: friendName,
      });
      setTokens((t) => ({ ...t, [result.invite_id]: result.token }));
      capture("bring_friend_invite_created", {
        event_id: eventId,
        invite_id: result.invite_id,
      });
      setFriendName("");
      toast({
        title: "Invite ready",
        description: "Send the link to your friend via WhatsApp or copy it.",
      });
    } catch (err: any) {
      toast({
        title: "Could not create invite",
        description: err.message,
        variant: "destructive",
      });
    }
  };

  const handleGetLink = async (inviteId: string) => {
    try {
      const result = await remintMutation.mutateAsync(inviteId);
      setTokens((t) => ({ ...t, [inviteId]: result.token }));
      capture("bring_friend_invite_reshared", {
        event_id: eventId,
        invite_id: inviteId,
      });
    } catch (err: any) {
      toast({
        title: "Could not create link",
        description: err.message,
        variant: "destructive",
      });
    }
  };

  const handleShare = (invite: FriendInviteRow) => {
    const token = tokens[invite.id];
    if (!token) return;
    shareBringFriendInvite({
      token,
      eventTitle,
      friendName: invite.friend_first_name,
      expiresAt: invite.expires_at,
    });
  };

  const handleCopy = async (inviteId: string) => {
    const token = tokens[inviteId];
    if (!token) return;
    const link = buildBringFriendInviteLink(token);
    try {
      await navigator.clipboard.writeText(link);
      toast({ title: "Link copied" });
    } catch {
      window.prompt("Copy this invite link:", link);
    }
  };

  const handleRevoke = async (inviteId: string) => {
    try {
      await revokeMutation.mutateAsync(inviteId);
      capture("bring_friend_invite_revoked", {
        event_id: eventId,
        invite_id: inviteId,
      });
      toast({ title: "Invite revoked" });
    } catch (err: any) {
      toast({
        title: "Could not revoke",
        description: err.message,
        variant: "destructive",
      });
    }
  };

  const handleRename = async (inviteId: string) => {
    const name = editingName.trim();
    if (!isFullName(name)) return;
    try {
      await updateMutation.mutateAsync({ inviteId, friendFullName: name });
      setEditingId(null);
      setEditingName("");
      toast({ title: "Name updated" });
    } catch (err: any) {
      toast({
        title: "Could not update name",
        description: err.message,
        variant: "destructive",
      });
    }
  };

  const formatExpiry = (expiresAt: string) => {
    const diff = new Date(expiresAt).getTime() - Date.now();
    const hours = Math.ceil(diff / (1000 * 60 * 60));
    if (hours <= 0) return "Expired";
    if (hours < 24) return `Expires in ${hours} hour${hours === 1 ? "" : "s"}`;
    const days = Math.ceil(hours / 24);
    return `Expires in ${days} day${days === 1 ? "" : "s"}`;
  };

  return (
    <div
      id="bring-friend-panel"
      className={embedded
        ? "text-left pt-4 border-t border-border"
        : "bg-card border border-border rounded-lg p-4 shadow-sm mb-4 text-left"
      }
    >
      <div className="flex items-center gap-2 mb-1">
        <Gift className="h-5 w-5 text-primary" />
        <h3 className="font-semibold text-foreground">
          Bring a Friend for Free
        </h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Invite up to {maxSeats} friends who are new to Nimi — their
        ticket is on us.{" "}
        {remainingSlots > 0
          ? `${remainingSlots} invite${remainingSlots === 1 ? "" : "s"} left.`
          : "All invites used."}
      </p>

      <div className="space-y-2">
        {eventInvites.map((invite) => {
          const token = tokens[invite.id];

          if (invite.status === "claimed") {
            return (
              <div
                key={invite.id}
                className="flex items-center justify-between p-3 rounded-lg border border-border bg-success/5"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-success/20 flex items-center justify-center">
                    <Check className="h-4 w-4 text-success" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-foreground">
                      {invite.friend_first_name || "Your friend"}
                    </span>
                    <Badge variant="secondary" className="ml-2 text-xs">
                      Joined
                    </Badge>
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div
              key={invite.id}
              className="p-3 rounded-lg border border-border space-y-2"
            >
              <div className="flex items-start gap-2">
                <div className="w-8 h-8 shrink-0 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                  <Clock className="h-4 w-4 text-primary" />
                </div>
                {editingId === invite.id ? (
                  <div className="flex items-center gap-1 min-w-0 flex-1">
                    <Input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      placeholder="Friend's full name"
                      maxLength={80}
                      className="h-8 w-40"
                    />
                    <Button
                      size="sm"
                      onClick={() => handleRename(invite.id)}
                      disabled={updateMutation.isPending || !isFullName(editingName)}
                    >
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingId(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="min-w-0 flex-1">
                    <button
                      type="button"
                      className="min-w-0 max-w-full text-left text-sm font-medium text-foreground inline-flex items-center gap-1 hover:underline"
                      onClick={() => {
                        setEditingId(invite.id);
                        setEditingName(invite.friend_first_name || "");
                      }}
                    >
                      <span className="truncate">
                        {invite.friend_first_name || "Invited"}
                      </span>
                      <Pencil className="h-3 w-3 text-muted-foreground shrink-0" />
                    </button>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      {formatExpiry(invite.expires_at)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-end gap-1 shrink-0 mt-0.5">
                  {token ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 w-8 p-0"
                        title="Share on WhatsApp"
                        aria-label="Share on WhatsApp"
                        onClick={() => handleShare(invite)}
                      >
                        <Share2 className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 w-8 p-0"
                        title="Copy link"
                        aria-label="Copy link"
                        onClick={() => handleCopy(invite.id)}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
                        title="Cancel invite"
                        aria-label="Cancel invite"
                        onClick={() => handleRevoke(invite.id)}
                        disabled={revokeMutation.isPending}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8"
                      title="Get invite links"
                      onClick={() => handleGetLink(invite.id)}
                      disabled={remintMutation.isPending}
                    >
                      <LinkIcon className="h-3.5 w-3.5 mr-1.5" />
                      Get invite links
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {remainingSlots > 0 && (
          <div className="p-3 rounded-lg border border-dashed border-border space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                <UserPlus className="h-4 w-4 text-muted-foreground" />
              </div>
              <span className="text-sm text-muted-foreground">
                Who are you inviting?
              </span>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                value={friendName}
                onChange={(e) => setFriendName(e.target.value)}
                placeholder="Friend's full name"
                maxLength={80}
              />
              <Button
                className="shrink-0"
                onClick={handleCreateInvite}
                disabled={reserveMutation.isPending || !isFullName(friendName)}
              >
                Create invite
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
